// src/components/AttendanceRecord.js
import React, { useState, useEffect, useRef } from 'react';
import Modal from './Modal';
import { Edit, Calendar, ChevronDown, MoreVertical } from 'lucide-react';
import * as dateUtils from '../utils/dateUtils';
import InternalWorkModal from './InternalWorkModal';

function AttendanceRecord({ userInfo }) {
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [records, setRecords] = useState([]);
  const [shifts, setShifts] = useState({});
  const [message, setMessage] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAbsenceModalOpen, setIsAbsenceModalOpen] = useState(false);
  const [isInternalWorkModalOpen, setIsInternalWorkModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [internalWorkRecords, setInternalWorkRecords] = useState([]);
  const [workTypeFilter, setWorkTypeFilter] = useState('all');
  const [editData, setEditData] = useState({
    clock_in: '',
    clock_out: '',
    note: '',
    modification_reason: '',
    break_time: '1',
    irregularType: 'absence',
    reportedTo: ''
  });
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [openMenuRow, setOpenMenuRow] = useState(null); // ドロップダウンメニューの開閉状態管理
  const menuRef = useRef(null); // メニューの外側クリック検知用
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false); // 削除確認ダイアログの状態
  const [deleteTarget, setDeleteTarget] = useState(null); // 削除対象の情報

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // メニューの外側クリックでメニューを閉じる
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuRow(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // シフトデータを取得
  const fetchShifts = async (yearMonth) => {
    try {
      const response = await fetch(`/api/shifts/${yearMonth}`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('シフトデータの取得に失敗しました');
      }

      const data = await response.json();
      
      // シフトデータを日付ごとに整形
      const formattedShifts = {};
      
      data.forEach(shift => {
        // 統一されたDB日付変換関数を使用
        const dateStr = dateUtils.formatDBDateToJST(shift.date);
        console.log('Original shift date:', shift.date);
        console.log('Converted shift date to JST:', dateStr);
        
        formattedShifts[dateStr] = {
          startTime: shift.start_time,
          endTime: shift.end_time
        };
      });
      
      setShifts(formattedShifts);
    } catch (error) {
      console.error('シフトデータの取得エラー:', error);
    }
  };

  // 勤怠記録を取得
  const fetchAttendanceRecords = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/time-records?month=${selectedMonth}`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('勤怠記録の取得に失敗しました');
      }

      const data = await response.json();

      // 日付ごとにデータを整形
      const formattedData = {};
      
      data.forEach(record => {
        if (!record || !record.recorded_at) return;
        
        // dateUtils.jsを使用して日付を変換
        const dateStr = dateUtils.formatDBDateToJST(record.recorded_at);
        
        if (!formattedData[dateStr]) {
          formattedData[dateStr] = {
            date: dateStr,
            records: []
          };
        }
        
        formattedData[dateStr].records.push(record);
      });
      
      // 各日付の出勤・退勤時間を特定
      Object.keys(formattedData).forEach(dateStr => {
        const dayRecords = formattedData[dateStr].records;
        let clockIn = null;
        let clockOut = null;
        let note = '';
        let absence_reason = null;
        let break_time = '1:00';
        
        // 通常の打刻記録があるかどうかを確認
        const hasNormalRecords = dayRecords.some(record => 
          record.record_type === 'clock_in' || record.record_type === 'in' ||
          record.record_type === 'clock_out' || record.record_type === 'out'
        );
        
        // 欠勤記録を日付でソートして最新のものを取得（通常の打刻記録がない場合のみ）
        if (!hasNormalRecords) {
          const absenceRecords = dayRecords.filter(record => record.record_type === 'absence');
          
          if (absenceRecords.length > 0) {
            // created_atで降順ソート（最新のものが先頭に）
            const sortedAbsenceRecords = absenceRecords.sort((a, b) => {
              return new Date(b.created_at) - new Date(a.created_at);
            });
            
            // 最新の欠勤記録を使用
            const latestAbsence = sortedAbsenceRecords[0];
            absence_reason = latestAbsence.absence_reason;
            note = latestAbsence.note || '';
          }
        }
        
        // 出退勤記録の処理
        const inRecords = dayRecords.filter(record => 
          record.record_type === 'clock_in' || record.record_type === 'in'
        );
        const outRecords = dayRecords.filter(record => 
          record.record_type === 'clock_out' || record.record_type === 'out'
        );
        
        // 出勤記録の処理 - 最も早い時間を選択
        if (inRecords.length > 0) {
          // 時間でソート（昇順）
          const sortedInRecords = [...inRecords].sort((a, b) => {
            const timeA = new Date(a.recorded_at).getTime();
            const timeB = new Date(b.recorded_at).getTime();
            return timeA - timeB;  // 昇順（早い時間が先）
          });
          
          // 最も早い出勤記録を使用
          const earliestRecord = sortedInRecords[0];
          clockIn = dateUtils.formatTimeToJST(earliestRecord.recorded_at);
        }
        
        // 退勤記録の処理 - 最も遅い時間を選択
        if (outRecords.length > 0) {
          // 時間でソート（降順）
          const sortedOutRecords = [...outRecords].sort((a, b) => {
            const timeA = new Date(a.recorded_at).getTime();
            const timeB = new Date(b.recorded_at).getTime();
            return timeB - timeA;  // 降順（遅い時間が先）
          });
          
          // 最も遅い退勤記録を使用
          const latestRecord = sortedOutRecords[0];
          clockOut = dateUtils.formatTimeToJST(latestRecord.recorded_at);
        }
        
        // 欠勤記録がない場合のみ、通常の打刻のnoteを使用
        if (!absence_reason) {
          const noteRecord = [...inRecords, ...outRecords].find(record => record.note);
          if (noteRecord) {
            note = noteRecord.note;
          }
        }
        
        // 休憩時間の処理 - 最新の値を優先（修正された値を使用）
        const allRecords = [...inRecords, ...outRecords];
        // 最新の修正時刻を持つレコードを検索
        const latestModifiedRecord = allRecords
          .filter(record => record.break_time !== null && record.break_time !== undefined)
          .sort((a, b) => {
            const aTime = new Date(a.last_modified_at || a.recorded_at).getTime();
            const bTime = new Date(b.last_modified_at || b.recorded_at).getTime();
            return bTime - aTime; // 新しい順
          })[0];
        
        console.log('休憩時間処理 - 日付:', dateStr, '対象レコード:', latestModifiedRecord);
        if (latestModifiedRecord) {
          break_time = `${Math.floor(latestModifiedRecord.break_time / 60)}:${latestModifiedRecord.break_time % 60 < 10 ? '0' + (latestModifiedRecord.break_time % 60) : latestModifiedRecord.break_time % 60}`;
          console.log('休憩時間設定 - 日付:', dateStr, '休憩時間:', break_time);
        } else {
          break_time = null;
          console.log('休憩時間null設定 - 日付:', dateStr);
        }
        
        // 欠勤の場合は休憩時間をnullに設定
        if (absence_reason) {
          break_time = null;
        }
        
        formattedData[dateStr].clock_in = clockIn;
        formattedData[dateStr].clock_out = clockOut;
        formattedData[dateStr].note = note;
        formattedData[dateStr].absence_reason = absence_reason;
        formattedData[dateStr].break_time = break_time;
      });
      
      // 日付でソート
      const sortedRecords = Object.values(formattedData).sort((a, b) => {
        return new Date(a.date) - new Date(b.date);
      });
      
      setRecords(sortedRecords);
      
      // 社内業務記録も取得
      await fetchInternalWorkRecords();
    } catch (error) {
      console.error('Error fetching attendance records:', error);
      setMessage('勤怠記録の取得に失敗しました: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 社内業務記録を取得
  const fetchInternalWorkRecords = async () => {
    try {
      const response = await fetch(`/api/internal-work?month=${selectedMonth}`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('社内業務記録の取得に失敗しました');
      }

      const data = await response.json();
      
      // 日付の処理を修正
      const formattedData = data.map(record => {
        // 日付が正しいフォーマットであることを確認
        if (record.date) {
          // 日付のフォーマットを確認
          console.log('社内業務記録の日付:', record.date);
          
          // 日付が既にJST形式であることを確認
          const dateObj = new Date(record.date);
          console.log('変換後の日付オブジェクト:', dateObj);
          
          // 日付を正規化（YYYY-MM-DD形式に）
          // dateUtils.jsのformatDBDateToJST関数を使用
          const normalizedDate = dateUtils.formatDBDateToJST(record.date);
          console.log('正規化された日付:', normalizedDate);
          
          return {
            ...record,
            // 正規化された日付を使用
            date: normalizedDate
          };
        }
        return record;
      });
      
      setInternalWorkRecords(formattedData);
    } catch (error) {
      console.error('Error fetching internal work records:', error);
      setMessage('社内業務記録の取得に失敗しました: ' + error.message);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchAttendanceRecords(),
          fetchShifts(selectedMonth),
          fetchInternalWorkRecords()
        ]);
      } catch (error) {
        console.error('データ取得エラー:', error);
        setMessage('データの取得に失敗しました');
      }
    };

    fetchData();

    // 定期的な更新を設定（1分ごと）
    const intervalId = setInterval(fetchData, 60000);

    return () => clearInterval(intervalId);
  }, [selectedMonth]);

  const isWeekend = (dateStr) => {
    return dateUtils.isWeekend(dateStr);
  };

  // 月の日数を取得する関数
  const getDaysInMonth = (yearMonth) => {
    const [year, month] = yearMonth.split('-').map(Number);
    return new Date(year, month, 0).getDate();
  };

  // 日付の配列を生成する関数
  const generateDatesForMonth = (yearMonth) => {
    const [year, month] = yearMonth.split('-').map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();
    const dates = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const paddedDay = day.toString().padStart(2, '0');
      const dateStr = `${yearMonth}-${paddedDay}`;
      dates.push(dateStr);
    }

    return dates;
  };

  // 日付ごとのレコードを処理して表示用のデータを作成
  const processRecordsForDisplay = () => {
    const dates = generateDatesForMonth(selectedMonth);
    const processedRecords = {};

    dates.forEach(dateStr => {
      // 日付に対応する勤怠記録を取得
      const recordsForDate = records.filter(record => {
        if (!record.date) return false;
        return record.date === dateStr;
      });

      // シフト情報を取得
      const shift = shifts[dateStr] || {};

      // 表示用のデータを作成
      processedRecords[dateStr] = recordsForDate.length > 0 ? recordsForDate[0] : {
        date: dateStr,
        clock_in: null,
        clock_out: null,
        break_time: null,
        note: '',
        absence_reason: null,
        records: [],
        shift: shift
      };
    });

    return processedRecords;
  };

  // 時間文字列を分に変換するヘルパー関数
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // 分を時間文字列に変換するヘルパー関数
  const minutesToTime = (minutes) => {
    if (minutes <= 0) return null;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}:${mins < 10 ? '0' + mins : mins}`;
  };

  // フィルタリングされたレコードを取得
  const getFilteredRecords = () => {
    const processedRecords = processRecordsForDisplay();
    const dates = generateDatesForMonth(selectedMonth);
    
    // フィルタリング
    const filteredDates = dates.filter(dateStr => {
      const record = processedRecords[dateStr];
      
      if (workTypeFilter === 'internal') {
        // 社内業務のみ表示
        const internalWork = internalWorkRecords.find(r => {
          if (!r.date) return false;
          const recordDate = dateUtils.formatDBDateToJST(r.date);
          return recordDate === dateStr;
        });
        return internalWork;
      } else if (workTypeFilter === 'client') {
        // クライアント業務のみ表示
        // 社内業務がある日も表示するように修正（日付自体は表示する）
        return true;
      }
      
      // 'all'の場合はすべての日付を表示
      return true;
    });
    
    // フィルタリング後のレコードを調整
    const adjustedRecords = { ...processedRecords };
    
    if (workTypeFilter === 'internal') {
      // 社内業務のみの場合、社内業務の時間を出勤・退勤として表示
      filteredDates.forEach(dateStr => {
        const internalWork = internalWorkRecords.find(r => {
          if (!r.date) return false;
          const recordDate = dateUtils.formatDBDateToJST(r.date);
          return recordDate === dateStr;
        });
        if (internalWork && adjustedRecords[dateStr]) {
          adjustedRecords[dateStr] = {
            ...adjustedRecords[dateStr],
            clock_in: internalWork.start_time.substring(0, 5),
            clock_out: internalWork.end_time.substring(0, 5),
            note: internalWork.note || adjustedRecords[dateStr].note,
            is_internal_work: true
          };
        }
      });
    } else if (workTypeFilter === 'client') {
      // クライアント業務のみの場合、社内業務の時間を差し引く
      filteredDates.forEach(dateStr => {
        const record = adjustedRecords[dateStr];
        const internalWork = internalWorkRecords.find(r => {
          if (!r.date) return false;
          const recordDate = dateUtils.formatDBDateToJST(r.date);
          return recordDate === dateStr;
        });
        
        // 社内業務がある場合のみ調整
        if (record && record.clock_in && record.clock_out && internalWork) {
          // 出勤・退勤時間を分に変換
          const clockInMinutes = timeToMinutes(record.clock_in);
          const clockOutMinutes = timeToMinutes(record.clock_out);
          
          // 社内業務の時間を分に変換
          const internalStartMinutes = timeToMinutes(internalWork.start_time.substring(0, 5));
          const internalEndMinutes = timeToMinutes(internalWork.end_time.substring(0, 5));
          
          // 社内業務の時間を差し引く
          let adjustedClockIn = clockInMinutes;
          let adjustedClockOut = clockOutMinutes;
          
          // 社内業務が出勤時間より後に始まる場合
          if (internalStartMinutes > clockInMinutes) {
            adjustedClockIn = clockInMinutes;
            
            // 社内業務が退勤時間より前に終わる場合
            if (internalEndMinutes < clockOutMinutes) {
              // 社内業務の時間を差し引く（出勤時間は変わらず、退勤時間から社内業務時間を引く）
              const internalDuration = internalEndMinutes - internalStartMinutes;
              adjustedClockOut = clockOutMinutes - internalDuration;
            } else {
              // 社内業務が退勤時間まで続く場合、出勤時間から社内業務開始までを表示
              adjustedClockOut = internalStartMinutes;
            }
          } else {
            // 社内業務が出勤時間から始まる場合
            if (internalEndMinutes < clockOutMinutes) {
              // 社内業務終了後から退勤までを表示
              adjustedClockIn = internalEndMinutes;
              adjustedClockOut = clockOutMinutes;
            } else {
              // 社内業務が全時間を占める場合、クライアント業務なし
              adjustedClockIn = null;
              adjustedClockOut = null;
            }
          }
          
          // 調整した時間を設定
          adjustedRecords[dateStr] = {
            ...record,
            clock_in: adjustedClockIn ? minutesToTime(adjustedClockIn) : null,
            clock_out: adjustedClockOut ? minutesToTime(adjustedClockOut) : null,
            is_client_work: true
          };
        }
      });
    } else if (workTypeFilter === 'all') {
      // すべて表示の場合、社内業務がある日は実際の時間フィールドに社内業務の時間を表示
      filteredDates.forEach(dateStr => {
        const record = adjustedRecords[dateStr];
        const internalWork = internalWorkRecords.find(r => {
          if (!r.date) return false;
          const recordDate = dateUtils.formatDBDateToJST(r.date);
          return recordDate === dateStr;
        });
        
        // 社内業務がある場合、社内業務の時間を出勤・退勤・休憩時間として表示
        if (internalWork) {
          const internalStartTime = internalWork.start_time.substring(0, 5);
          const internalEndTime = internalWork.end_time.substring(0, 5);
          const internalBreakTime = internalWork.break_time ? `${Math.floor(internalWork.break_time / 60)}:${String(internalWork.break_time % 60).padStart(2, '0')}` : null;
          
          // 社内業務の時間を実際のフィールドに設定
          adjustedRecords[dateStr] = {
            ...record,
            clock_in: internalStartTime,
            clock_out: internalEndTime,
            break_time: internalBreakTime,
            note: internalWork.note || record?.note,
            is_internal_work: true,
            has_internal_work: true
          };
        }
      });
    }
    
    return { dates: filteredDates, processedRecords: adjustedRecords };
  };

  // フィルタリングされたレコードを取得
  const { dates, processedRecords } = getFilteredRecords();

  // メニューの開閉制御
  const toggleMenu = (dateStr) => {
    setOpenMenuRow(openMenuRow === dateStr ? null : dateStr);
  };

  // ドロップダウンメニューアクションハンドラー
  const handleMenuAction = (action, record, dateStr) => {
    setOpenMenuRow(null); // メニューを閉じる
    
    switch(action) {
      case 'edit':
        handleEditClick(record, dateStr);
        break;
      case 'irregular':
        handleIrregularClick(dateStr);
        break;
      case 'internal':
        handleInternalWorkClick(dateStr);
        break;
    }
  };

  // 遅刻判定のヘルパー関数
  const isLate = (clockInTime, shiftStartTime) => {
    if (!clockInTime || !shiftStartTime) return false;
    
    // 時間文字列をHH:MM形式に統一
    const clockIn = clockInTime.substring(0, 5); // "HH:MM"
    const shiftStart = shiftStartTime.substring(0, 5); // "HH:MM"
    
    // 時間と分を分離して数値に変換
    const [clockInHour, clockInMin] = clockIn.split(':').map(Number);
    const [shiftStartHour, shiftStartMin] = shiftStart.split(':').map(Number);
    
    // 分単位で比較
    const clockInMinutes = clockInHour * 60 + clockInMin;
    const shiftStartMinutes = shiftStartHour * 60 + shiftStartMin;
    
    return clockInMinutes > shiftStartMinutes;
  };

  // 修正ボタンのハンドラー
  const handleEditClick = (record, dateStr) => {
    console.log('修正対象のレコード:', record);
    console.log('record.id:', record.id, 'タイプ:', typeof record.id);
    
    setSelectedRecord({ date: dateStr, id: record.id });
    
    // 表示用のデータを作成
    const recordData = {
      date: dateStr,
      clock_in: record.clock_in || '',
      clock_out: record.clock_out || '',
      note: record.note || '',
      break_time: record.break_time || '1'
    };
    
    // 時間をHTML time input形式（HH:MM）に変換
    let clockInTime = '';
    if (recordData.clock_in) {
      // 日本時間表記から時間と分を抽出
      const timeParts = recordData.clock_in.split(':');
      if (timeParts.length >= 2) {
        clockInTime = `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}`;
      }
    }
    
    let clockOutTime = '';
    if (recordData.clock_out) {
      // 日本時間表記から時間と分を抽出
      const timeParts = recordData.clock_out.split(':');
      if (timeParts.length >= 2) {
        clockOutTime = `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}`;
      }
    }
    
    // 休憩時間の処理を安全に行う
    let breakTimeValue = '1'; // デフォルト値
    if (recordData.break_time) {
      if (recordData.break_time.includes(':')) {
        // "1:00" 形式の場合、時間部分を取得
        breakTimeValue = recordData.break_time.split(':')[0];
      } else {
        // 数値形式の場合はそのまま使用
        breakTimeValue = recordData.break_time.toString();
      }
    }

    setEditData({
      clock_in: clockInTime,
      clock_out: clockOutTime,
      note: recordData.note || '',
      modification_reason: '',
      break_time: breakTimeValue,
      irregularType: 'absence',
      reportedTo: ''
    });
    
    console.log('編集モーダルを開きます');
    setIsEditModalOpen(true);
  };

  // 欠勤ボタンのハンドラー → イレギュラーボタンのハンドラーに変更
  const handleIrregularClick = (dateStr) => {
    setSelectedRecord({ date: dateStr });
    setEditData({
      ...editData,
      note: '',
      irregularType: 'absence',
      reportedTo: ''
    });
    setIsAbsenceModalOpen(true);
  };

  // 修正を保存
  const handleSaveEdit = async () => {
    console.log('修正保存ボタンがクリックされました');
    
    if (!selectedRecord?.date) {
      console.error('修正対象の記録が見つかりません');
      setMessage('修正対象の記録が見つかりません');
      return;
    }
    
    if (!editData.modification_reason) {
      console.error('修正理由が入力されていません');
      setMessage('修正理由を入力してください');
      return;
    }

    // 入力チェック - 出勤時間または退勤時間のいずれかは必須
    if (!editData.clock_in && !editData.clock_out) {
      console.error('出勤時間または退勤時間を入力してください');
      setMessage('出勤時間または退勤時間を入力してください');
      return;
    }

    try {
      console.log('送信する修正データ:', {
        selectedRecord,
        editData
      });
      console.log('selectedRecord詳細:', selectedRecord);
      console.log('selectedRecord.id:', selectedRecord.id, 'タイプ:', typeof selectedRecord.id);
      
      // 対象日の最初のレコードIDを使用
      const recordId = selectedRecord.id || 'new';
      console.log('使用するレコードID:', recordId);
      
      // 時間フォーマットを確認
      let clockIn = editData.clock_in;
      let clockOut = editData.clock_out;
      
      // 秒が含まれていない場合は追加
      if (clockIn && clockIn.split(':').length === 2) {
        clockIn = `${clockIn}:00`;
      }
      
      if (clockOut && clockOut.split(':').length === 2) {
        clockOut = `${clockOut}:00`;
      }
      
      console.log('フォーマット後の時間:', { clockIn, clockOut });
      
      const requestBody = {
        date: selectedRecord.date,
        clock_in: clockIn,
        clock_out: clockOut,
        note: editData.note,
        modification_reason: editData.modification_reason,
        break_time: editData.break_time
      };
      
      console.log('リクエスト先URL:', `/api/time-records/${recordId}`);
      console.log('リクエストボディ:', requestBody);
      
      const response = await fetch(`/api/time-records/${recordId}`, {
        method: 'PUT',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      console.log('レスポンスステータス:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('エラーレスポンス:', errorData);
        throw new Error(errorData.message || '修正の保存に失敗しました');
      }

      const result = await response.json();
      console.log('修正結果:', result);
      
      setIsEditModalOpen(false);
      await fetchAttendanceRecords();
      setMessage('勤怠記録を更新しました');

    } catch (error) {
      console.error('修正保存エラー:', error);
      setMessage(error.message);
    }
  };

  // 削除確認ダイアログを表示
  const confirmDeleteField = (recordId, field, dateStr) => {
    const fieldNames = {
      'clock_in': '出勤時間',
      'clock_out': '退勤時間', 
      'break_time': '休憩時間',
      'note': '備考'
    };
    
    setDeleteTarget({
      recordId,
      field,
      dateStr,
      fieldName: fieldNames[field] || field
    });
    setShowDeleteConfirm(true);
  };

  // 確認ダイアログからの削除実行
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    
    setShowDeleteConfirm(false);
    await executeDeleteField(deleteTarget.recordId, deleteTarget.field);
    setDeleteTarget(null);
  };

  // 削除キャンセル
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };

  // 特定のフィールドを削除するハンドラー
  const executeDeleteField = async (recordId, field) => {
    try {
      if (!recordId) {
        setMessage('削除対象の記録が見つかりません');
        return;
      }

      // 管理者権限チェック
      if (!userInfo || (userInfo.role !== 'admin' && userInfo.role !== 'super_admin')) {
        setMessage('この操作を行う権限がありません');
        console.log('権限エラー: 管理者権限が必要です', userInfo);
        return;
      }

      console.log(`${field}を削除します:`, recordId);

      // APIリクエスト - 自分自身のデータなので、userIdは不要
      const url = `/api/time-records/${recordId}/field`;
      console.log('削除リクエスト先URL:', url);
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ field })
      });
      
      console.log('削除レスポンスステータス:', response.status);
      
      if (!response.ok) {
        // レスポンスのContent-Typeをチェック
        const contentType = response.headers.get('content-type');
        let errorMessage = '';
        
        if (contentType && contentType.includes('application/json')) {
          // JSONレスポンスの場合
          const errorData = await response.json();
          console.log('削除エラーレスポンス (JSON):', errorData);
          errorMessage = errorData.message || `${field}の削除に失敗しました`;
        } else {
          // テキストまたはその他のレスポンスの場合
          const errorText = await response.text();
          console.log('削除エラーレスポンス (テキスト):', errorText);
          errorMessage = `${field}の削除に失敗しました: サーバーエラー`;
        }
        
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      console.log('削除結果:', result);
      
      // 削除処理後にデータを再取得
      console.log('削除処理完了、データを再取得します');
      await fetchAttendanceRecords(); // 勤怠記録を再取得
      console.log('データ再取得完了');
      
      setMessage(`${field === 'clock_in' ? '出勤時間' : field === 'clock_out' ? '退勤時間' : field === 'break_time' ? '休憩時間' : '備考'}を削除しました`);
    } catch (err) {
      console.log('削除エラー:', err);
      setMessage(`${field}の削除に失敗しました: ` + err.message);
    }
  };

  // 社内業務の削除ハンドラー
  const handleDeleteInternalWork = async (recordId) => {
    try {
      if (!recordId) {
        setMessage('削除対象の記録が見つかりません');
        return;
      }

      // 管理者権限チェック
      if (!userInfo || (userInfo.role !== 'admin' && userInfo.role !== 'super_admin')) {
        setMessage('この操作を行う権限がありません');
        console.log('権限エラー: 管理者権限が必要です', userInfo);
        return;
      }

      console.log('社内業務を削除します:', recordId);

      // APIリクエスト
      const url = `/api/internal-work/${recordId}`;
      console.log('削除リクエスト先URL:', url);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });
      
      console.log('削除レスポンスステータス:', response.status);
      
      if (!response.ok) {
        // レスポンスのContent-Typeをチェック
        const contentType = response.headers.get('content-type');
        let errorMessage = '';
        
        if (contentType && contentType.includes('application/json')) {
          // JSONレスポンスの場合
          const errorData = await response.json();
          console.log('削除エラーレスポンス (JSON):', errorData);
          errorMessage = errorData.message || '社内業務の削除に失敗しました';
        } else {
          // テキストまたはその他のレスポンスの場合
          const errorText = await response.text();
          console.log('削除エラーレスポンス (テキスト):', errorText);
          errorMessage = '社内業務の削除に失敗しました: サーバーエラー';
        }
        
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      console.log('削除結果:', result);
      
      setMessage('社内業務を削除しました');
      fetchAttendanceRecords(); // 勤怠記録を再取得
      fetchInternalWorkRecords(); // 社内業務記録を再取得
    } catch (err) {
      console.log('削除エラー:', err);
      setMessage('社内業務の削除に失敗しました: ' + err.message);
    }
  };

  // 欠勤を保存 → イレギュラーを保存
  const handleSaveAbsence = async () => {
    try {
      // 備考欄に表示する内容を整形
      const irregularTypeText = {
        'late': '遅刻',
        'early': '早退',
        'absence': '欠勤'
      }[editData.irregularType];
      
      // 報告先をそのまま使用（ユーザーが入力した通りに表示）
      const reportedTo = editData.reportedTo ? editData.reportedTo.trim() : '';
      // 「報告先:」という文字列を追加しない
      const formattedReportedTo = reportedTo ? ` - ${reportedTo}` : '';
      
      const formattedNote = `${irregularTypeText}: ${editData.note}${formattedReportedTo}`;
      
      const response = await fetch('/api/time-records/absence', {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date: selectedRecord.date,
          reason: formattedNote,
          irregularType: editData.irregularType
        })
      });

      if (!response.ok) throw new Error('イレギュラーの登録に失敗しました');

      setIsAbsenceModalOpen(false);
      await fetchAttendanceRecords();
    } catch (error) {
      console.error('イレギュラー登録エラー:', error);
      setMessage('イレギュラーの登録に失敗しました');
    }
  };

  // 社内業務ボタンのクリックハンドラ
  const handleInternalWorkClick = (dateStr) => {
    // 選択された日付の勤怠記録を取得
    const record = processedRecords[dateStr] || {};
    const recordsForDate = record.records || [];
    
    // 出勤・退勤時間を特定
    let clockInTime = record.clock_in;
    let clockOutTime = record.clock_out;

    // 既存の社内業務記録を確認
    const existingRecord = record.internal_work;

    setSelectedRecord({
      date: dateStr,
      clockInTime,
      clockOutTime,
      existingRecord
    });

    setIsInternalWorkModalOpen(true);
  };

  // 社内業務申請の送信
  const handleInternalWorkSubmit = async (data) => {
    try {
      // 日付をJST形式で処理
      const jstDate = data.date;
      console.log('社内業務申請 - 元の日付:', jstDate);
      
      // 日付をUTC形式に変換せず、そのまま送信
      // JSTの日付をそのまま使用
      const requestData = {
        ...data,
        date: jstDate
      };
      
      console.log('社内業務申請データ:', requestData);
      
      const response = await fetch('/api/internal-work', {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '社内業務申請の送信に失敗しました');
      }

      const responseData = await response.json();
      console.log('社内業務申請結果:', responseData);
      
      setMessage('社内業務時間を登録しました');
      
      // 社内業務記録を再取得
      await fetchInternalWorkRecords();
    } catch (error) {
      console.error('Error submitting internal work:', error);
      setMessage('社内業務申請の送信に失敗しました: ' + error.message);
      throw error;
    }
  };

  return (
    <div className="container mx-auto p-4">
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <input
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="border rounded px-3 py-2"
        />
        
        {/* 業務タイプフィルタ */}
        <div className="flex items-center">
          <span className="mr-2 text-gray-700">業務タイプ:</span>
          <div className="flex border rounded overflow-hidden">
            <button
              className={`px-3 py-2 ${workTypeFilter === 'all' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`}
              onClick={() => setWorkTypeFilter('all')}
            >
              すべて
            </button>
            <button
              className={`px-3 py-2 ${workTypeFilter === 'client' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`}
              onClick={() => setWorkTypeFilter('client')}
            >
              クライアント業務
            </button>
            <button
              className={`px-3 py-2 ${workTypeFilter === 'internal' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700'}`}
              onClick={() => setWorkTypeFilter('internal')}
            >
              社内業務
            </button>
          </div>
        </div>
      </div>

      {message && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {message}
        </div>
      )}

      {/* デスクトップ表示 */}
      {!isMobile && (
        <table className="min-w-full bg-white border">
          <thead>
            <tr>
              <th className="px-4 py-2">日付</th>
              <th className="px-4 py-2">シフト</th>
              <th className="px-4 py-2">出勤</th>
              <th className="px-4 py-2">退勤</th>
              <th className="px-4 py-2">休憩時間</th>
              <th className="px-4 py-2">備考</th>
              <th className="px-4 py-2">操作</th>
            </tr>
          </thead>
          <tbody>
            {dates.map((dateStr, index) => {
              const date = dateUtils.createSafeDate(dateStr);
              const dayOfWeek = dateUtils.getDayOfWeek(dateStr);
              const shift = shifts[dateStr] || {};
              const record = processedRecords[dateStr] || {};
              
              // 社内業務記録を取得（操作ボタン用に残す）
              const internalWork = internalWorkRecords.find(r => {
                if (!r.date) return false;
                const recordDate = dateUtils.formatDBDateToJST(r.date);
                return recordDate === dateStr;
              });
              
              // 出勤・退勤レコードのIDを特定
              const recordsForDate = record.records || [];
              
              const inRecord = recordsForDate.find(r => r.record_type === 'in' || r.record_type === 'clock_in');
              const outRecord = recordsForDate.find(r => r.record_type === 'out' || r.record_type === 'clock_out');
              const inRecordId = inRecord ? inRecord.id : null;
              const outRecordId = outRecord ? outRecord.id : null;
              
              // 修正ボタン用のレコードオブジェクト（idを含む）
              const recordWithId = {
                ...record,
                id: outRecordId || inRecordId || null  // 退勤レコード優先、なければ出勤レコード
              };
              
              // 休憩時間と備考のレコードIDを特定
              const breakTimeRecordId = outRecord ? outRecord.id : (inRecord ? inRecord.id : null);
              const noteRecordId = outRecord ? outRecord.id : (inRecord ? inRecord.id : null);

              return (
                <tr key={index} className={isWeekend(dateStr) ? 'bg-gray-100' : ''}>
                  <td className="border px-4 py-2">
                    {`${date.getDate()}日(${dayOfWeek})`}
                  </td>
                  <td className="border px-4 py-2">
                    {shift.startTime && shift.endTime ? 
                      `${shift.startTime.substring(0, 5)} - ${shift.endTime.substring(0, 5)}` : 
                      '---'}
                  </td>
                  <td className="border px-4 py-2 relative">
                    {record.clock_in ? (
                      <div className="flex flex-col">
                        <div className="flex items-center">
                          <span className={isLate(record.clock_in, shift.startTime) ? 'text-red-500 font-semibold' : ''}>
                            {record.clock_in}
                          </span>
                          {inRecordId && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && !record.absence_reason && !record.is_internal_work && (
                            <button 
                              onClick={() => confirmDeleteField(inRecordId, 'clock_in', dateStr)}
                              className="ml-2 text-red-500 hover:text-red-700"
                              title="出勤時間を削除"
                            >
                              ×
                            </button>
                          )}
                          {record.is_internal_work && internalWork && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && (
                            <button 
                              onClick={() => handleDeleteInternalWork(internalWork.id)}
                              className="ml-2 text-red-500 hover:text-red-700"
                              title="社内業務を削除"
                            >
                              ×
                            </button>
                          )}
                        </div>
                        {/* 出勤時のGPS情報を表示 */}
                        {recordsForDate && (() => {
                          const inRecord = recordsForDate.find(r => 
                            (r.record_type === 'in' || r.record_type === 'clock_in') &&
                            r.latitude && r.longitude
                          );
                          if (inRecord && inRecord.address_data && inRecord.address_data.results && inRecord.address_data.results[0]) {
                            const fullAddress = inRecord.address_data.results[0].formatted_address;
                            const cleanAddress = fullAddress.replace(/^日本、〒\d{3}-\d{4}\s*/, '');
                            const mapUrl = `https://maps.google.com/maps?q=${inRecord.latitude},${inRecord.longitude}`;
                            return (
                              <a 
                                href={mapUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-gray-500 hover:text-blue-600 mt-0.5 block truncate max-w-32"
                                title={cleanAddress}
                              >
                                📍 {cleanAddress.length > 15 ? cleanAddress.substring(0, 15) + '...' : cleanAddress}
                              </a>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    ) : '---'}
                  </td>
                  <td className="border px-4 py-2 relative">
                    {record.clock_out ? (
                      <div className="flex flex-col">
                        <div className="flex items-center">
                          <span>{record.clock_out}</span>
                          {outRecordId && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && !record.absence_reason && !record.is_internal_work && (
                            <button 
                              onClick={() => confirmDeleteField(outRecordId, 'clock_out', dateStr)}
                              className="ml-2 text-red-500 hover:text-red-700"
                              title="退勤時間を削除"
                            >
                              ×
                            </button>
                          )}
                          {record.is_internal_work && internalWork && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && (
                            <button 
                              onClick={() => handleDeleteInternalWork(internalWork.id)}
                              className="ml-2 text-red-500 hover:text-red-700"
                              title="社内業務を削除"
                            >
                              ×
                            </button>
                          )}
                        </div>
                        {/* 退勤時のGPS情報を表示 */}
                        {recordsForDate && (() => {
                          const outRecord = recordsForDate.find(r => 
                            (r.record_type === 'out' || r.record_type === 'clock_out') &&
                            r.latitude && r.longitude
                          );
                          if (outRecord && outRecord.address_data && outRecord.address_data.results && outRecord.address_data.results[0]) {
                            const fullAddress = outRecord.address_data.results[0].formatted_address;
                            const cleanAddress = fullAddress.replace(/^日本、〒\d{3}-\d{4}\s*/, '');
                            const mapUrl = `https://maps.google.com/maps?q=${outRecord.latitude},${outRecord.longitude}`;
                            return (
                              <a 
                                href={mapUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-gray-500 hover:text-blue-600 mt-0.5 block truncate max-w-32"
                                title={cleanAddress}
                              >
                                📍 {cleanAddress.length > 15 ? cleanAddress.substring(0, 15) + '...' : cleanAddress}
                              </a>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    ) : '---'}
                  </td>
                  <td className="border px-4 py-2 relative">
                    {record.break_time && !record.absence_reason ? (
                      <div className="flex items-center">
                        <span>{record.break_time}</span>
                        {breakTimeRecordId && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && !record.is_internal_work && (
                          <button 
                            onClick={() => confirmDeleteField(breakTimeRecordId, 'break_time', dateStr)}
                            className="ml-2 text-red-500 hover:text-red-700"
                            title="休憩時間を削除"
                          >
                            ×
                          </button>
                        )}
                        {record.is_internal_work && internalWork && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && (
                          <button 
                            onClick={() => handleDeleteInternalWork(internalWork.id)}
                            className="ml-2 text-red-500 hover:text-red-700"
                            title="社内業務を削除"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ) : (
                      <span>---</span>
                    )}
                  </td>
                  <td className="border px-4 py-2 relative">
                    {record.note ? (
                      <div className="flex items-center">
                        <span>{record.note}</span>
                        {noteRecordId && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && !record.is_internal_work && (
                          <button 
                            onClick={() => confirmDeleteField(noteRecordId, 'note', dateStr)}
                            className="ml-2 text-red-500 hover:text-red-700"
                            title="備考を削除"
                          >
                            ×
                          </button>
                        )}
                        {record.is_internal_work && internalWork && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && (
                          <button 
                            onClick={() => handleDeleteInternalWork(internalWork.id)}
                            className="ml-2 text-red-500 hover:text-red-700"
                            title="社内業務を削除"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ) : '---'}
                  </td>
                  <td className="border px-4 py-2">
                    <div className="relative" ref={openMenuRow === dateStr ? menuRef : null}>
                      <button
                        onClick={() => toggleMenu(dateStr)}
                        className="bg-blue-500 text-white px-3 py-1 rounded text-sm flex items-center gap-1 hover:bg-blue-600"
                      >
                        <MoreVertical className="w-4 h-4" />
                        メニュー
                        <ChevronDown className={`w-4 h-4 transition-transform ${openMenuRow === dateStr ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {openMenuRow === dateStr && (
                        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-10 min-w-[120px]">
                          <button
                            onClick={() => handleMenuAction('edit', recordWithId, dateStr)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm border-b border-gray-200"
                          >
                            <Edit className="w-4 h-4 inline mr-2" />
                            修正
                          </button>
                          <button
                            onClick={() => handleMenuAction('irregular', recordWithId, dateStr)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm border-b border-gray-200"
                          >
                            <Calendar className="w-4 h-4 inline mr-2" />
                            イレギュラー
                          </button>
                          <button
                            onClick={() => handleMenuAction('internal', recordWithId, dateStr)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                          >
                            <Calendar className="w-4 h-4 inline mr-2" />
                            社内業務
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* モバイル表示 */}
      {isMobile && (
        <div className="space-y-4">
          {dates.map((dateStr, index) => {
            const date = dateUtils.createSafeDate(dateStr);
            const dayOfWeek = dateUtils.getDayOfWeek(dateStr);
            const shift = shifts[dateStr] || {};
            const record = processedRecords[dateStr] || {};
            
            // 社内業務記録を取得
            const internalWork = internalWorkRecords.find(r => {
              if (!r.date) return false;
              
              // 日付の比較方法を修正
              const recordDate = dateUtils.formatDBDateToJST(r.date);
              console.log(`社内業務日付比較: ${recordDate} vs ${dateStr}`);
              
              // 単純な文字列比較で日付を比較
              return recordDate === dateStr;
            });
            
            // 出勤・退勤レコードのIDを取得
            const recordsForDate = record.records || [];
            
            const inRecord = recordsForDate.find(r => r.record_type === 'in' || r.record_type === 'clock_in');
            const outRecord = recordsForDate.find(r => r.record_type === 'out' || r.record_type === 'clock_out');
            const inRecordId = inRecord ? inRecord.id : null;
            const outRecordId = outRecord ? outRecord.id : null;
            
            // 休憩時間と備考のレコードIDを特定
            const breakTimeRecordId = outRecord ? outRecord.id : (inRecord ? inRecord.id : null);
            const noteRecordId = outRecord ? outRecord.id : (inRecord ? inRecord.id : null);
            
            // 修正ボタン用のレコードオブジェクト（idを含む）
            const recordWithId = {
              ...record,
              id: outRecordId || inRecordId || null  // 退勤レコード優先、なければ出勤レコード
            };

            return (
              <div key={index} className={`p-4 border rounded ${isWeekend(dateStr) ? 'bg-gray-100' : 'bg-white'}`}>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-lg font-bold">{`${date.getDate()}日(${dayOfWeek})`}</h3>
                  <div className="relative" ref={openMenuRow === dateStr ? menuRef : null}>
                    <button
                      onClick={() => toggleMenu(dateStr)}
                      className="bg-blue-500 text-white px-2 py-1 rounded text-xs flex items-center gap-1 hover:bg-blue-600"
                    >
                      <MoreVertical className="w-3 h-3" />
                      メニュー
                      <ChevronDown className={`w-3 h-3 transition-transform ${openMenuRow === dateStr ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {openMenuRow === dateStr && (
                      <div className="absolute top-full right-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-10 min-w-[100px]">
                        <button
                          onClick={() => handleMenuAction('edit', recordWithId, dateStr)}
                          className="w-full text-left px-2 py-2 hover:bg-gray-100 text-xs border-b border-gray-200"
                        >
                          <Edit className="w-3 h-3 inline mr-1" />
                          修正
                        </button>
                        <button
                          onClick={() => handleMenuAction('irregular', recordWithId, dateStr)}
                          className="w-full text-left px-2 py-2 hover:bg-gray-100 text-xs border-b border-gray-200"
                        >
                          <Calendar className="w-3 h-3 inline mr-1" />
                          イレギュラー
                        </button>
                        <button
                          onClick={() => handleMenuAction('internal', recordWithId, dateStr)}
                          className="w-full text-left px-2 py-2 hover:bg-gray-100 text-xs"
                        >
                          <Calendar className="w-3 h-3 inline mr-1" />
                          社内業務
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* シフト情報 */}
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div>
                    <span className="text-gray-600 text-sm">シフト: </span>
                    <span>
                      {shift.startTime && shift.endTime ? 
                        `${shift.startTime.substring(0, 5)} - ${shift.endTime.substring(0, 5)}` : 
                        '---'}
                    </span>
                  </div>
                </div>
                
                {/* 出退勤情報 */}
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div className="relative">
                    <span className="text-gray-600 text-sm">出勤: </span>
                    <span className={`relative ${record.clock_in && isLate(record.clock_in, shift.startTime) ? 'text-red-500 font-semibold' : ''}`}>
                      {record.clock_in || '---'}
                    </span>
                    {record.clock_in && inRecordId && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && !record.absence_reason && !record.is_internal_work && (
                      <button 
                        onClick={() => confirmDeleteField(inRecordId, 'clock_in', dateStr)}
                        className="ml-1 text-red-500 hover:text-red-700 text-xs"
                        title="出勤時間を削除"
                      >
                        ×
                      </button>
                    )}
                    {record.is_internal_work && internalWork && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && (
                      <button 
                        onClick={() => handleDeleteInternalWork(internalWork.id)}
                        className="ml-1 text-red-500 hover:text-red-700 text-xs"
                        title="社内業務を削除"
                      >
                        ×
                      </button>
                    )}
                    {/* 出勤時のGPS情報を表示（モバイル版） */}
                    {recordsForDate && (() => {
                      const inRecord = recordsForDate.find(r => 
                        (r.record_type === 'in' || r.record_type === 'clock_in') &&
                        r.latitude && r.longitude
                      );
                      if (inRecord && inRecord.address_data && inRecord.address_data.results && inRecord.address_data.results[0]) {
                        const fullAddress = inRecord.address_data.results[0].formatted_address;
                        const cleanAddress = fullAddress.replace(/^日本、〒\d{3}-\d{4}\s*/, '');
                        const mapUrl = `https://maps.google.com/maps?q=${inRecord.latitude},${inRecord.longitude}`;
                        return (
                          <div className="mt-0.5">
                            <a 
                              href={mapUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-gray-500 hover:text-blue-600 block truncate"
                              title={cleanAddress}
                            >
                              📍 {cleanAddress.length > 20 ? cleanAddress.substring(0, 20) + '...' : cleanAddress}
                            </a>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  <div className="relative">
                    <span className="text-gray-600 text-sm">退勤: </span>
                    <span className="relative">
                      {record.clock_out || '---'}
                    </span>
                    {record.clock_out && outRecordId && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && !record.absence_reason && !record.is_internal_work && (
                      <button 
                        onClick={() => confirmDeleteField(outRecordId, 'clock_out', dateStr)}
                        className="ml-1 text-red-500 hover:text-red-700 text-xs"
                        title="退勤時間を削除"
                      >
                        ×
                      </button>
                    )}
                    {record.is_internal_work && internalWork && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && (
                      <button 
                        onClick={() => handleDeleteInternalWork(internalWork.id)}
                        className="ml-1 text-red-500 hover:text-red-700 text-xs"
                        title="社内業務を削除"
                      >
                        ×
                      </button>
                    )}
                    {/* 退勤時のGPS情報を表示（モバイル版） */}
                    {recordsForDate && (() => {
                      const outRecord = recordsForDate.find(r => 
                        (r.record_type === 'out' || r.record_type === 'clock_out') &&
                        r.latitude && r.longitude
                      );
                      if (outRecord && outRecord.address_data && outRecord.address_data.results && outRecord.address_data.results[0]) {
                        const fullAddress = outRecord.address_data.results[0].formatted_address;
                        const cleanAddress = fullAddress.replace(/^日本、〒\d{3}-\d{4}\s*/, '');
                        const mapUrl = `https://maps.google.com/maps?q=${outRecord.latitude},${outRecord.longitude}`;
                        return (
                          <div className="mt-0.5">
                            <a 
                              href={mapUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-gray-500 hover:text-blue-600 block truncate"
                              title={cleanAddress}
                            >
                              📍 {cleanAddress.length > 20 ? cleanAddress.substring(0, 20) + '...' : cleanAddress}
                            </a>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>

                {/* 休憩時間 */}
                <div className="mb-2 relative">
                  <span className="text-gray-600 text-sm">休憩時間: </span>
                  <span>
                    {record.break_time && !record.absence_reason ? record.break_time : '---'}
                  </span>
                  {record.break_time && !record.absence_reason && breakTimeRecordId && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && !record.is_internal_work && (
                    <button 
                      onClick={() => confirmDeleteField(breakTimeRecordId, 'break_time', dateStr)}
                      className="ml-1 text-red-500 hover:text-red-700 text-xs"
                      title="休憩時間を削除"
                    >
                      ×
                    </button>
                  )}
                  {record.is_internal_work && internalWork && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && (
                    <button 
                      onClick={() => handleDeleteInternalWork(internalWork.id)}
                      className="ml-1 text-red-500 hover:text-red-700 text-xs"
                      title="社内業務を削除"
                    >
                      ×
                    </button>
                  )}
                </div>
                
                {/* 備考 */}
                <div className="mb-2 relative">
                  <span className="text-gray-600 text-sm">備考: </span>
                  <span>
                    {record.note || '---'}
                  </span>
                  {record.note && noteRecordId && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && !record.is_internal_work && (
                    <button 
                      onClick={() => confirmDeleteField(noteRecordId, 'note', dateStr)}
                      className="ml-1 text-red-500 hover:text-red-700 text-xs"
                      title="備考を削除"
                    >
                      ×
                    </button>
                  )}
                  {record.is_internal_work && internalWork && userInfo && (userInfo.role === 'admin' || userInfo.role === 'super_admin') && (
                    <button 
                      onClick={() => handleDeleteInternalWork(internalWork.id)}
                      className="ml-1 text-red-500 hover:text-red-700 text-xs"
                      title="社内業務を削除"
                    >
                      ×
                    </button>
                  )}
                </div>
                
                {/* 欠勤情報 */}
                {record.absence_reason && (
                  <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                    <div className="text-sm font-medium text-red-800">欠勤理由: {record.absence_reason}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* 修正モーダル */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
        <div className="p-4">
          <h2 className="text-lg font-bold mb-4">打刻修正</h2>
          <div className="space-y-4">
            <div>
              <label className="block mb-1">出勤時刻</label>
              <input
                type="time"
                value={editData.clock_in}
                onChange={(e) => setEditData({...editData, clock_in: e.target.value})}
                className="border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block mb-1">退勤時刻</label>
              <input
                type="time"
                value={editData.clock_out}
                onChange={(e) => setEditData({...editData, clock_out: e.target.value})}
                className="border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block mb-1">休憩時間（時間）</label>
              <select
                value={editData.break_time || "1"}
                onChange={(e) => setEditData({...editData, break_time: e.target.value})}
                className="border rounded px-2 py-1"
              >
                <option value="0">0:00</option>
                <option value="0.5">0:30</option>
                <option value="1">1:00</option>
                <option value="1.5">1:30</option>
                <option value="2">2:00</option>
              </select>
            </div>
            <div>
              <label className="block mb-1">修正理由 <span className="text-red-500">*</span></label>
              <textarea
                value={editData.modification_reason}
                onChange={(e) => setEditData({...editData, modification_reason: e.target.value})}
                className="w-full border rounded px-2 py-1"
                required
              />
            </div>
            <div>
              <label className="block mb-1">備考</label>
              <textarea
                value={editData.note}
                onChange={(e) => setEditData({...editData, note: e.target.value})}
                className="w-full border rounded px-2 py-1"
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={() => setIsEditModalOpen(false)}
              className="px-4 py-2 border rounded"
            >
              キャンセル
            </button>
            <button
              onClick={handleSaveEdit}
              disabled={!editData.modification_reason}
              className="px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-300"
            >
              保存
            </button>
          </div>
        </div>
      </Modal>

      {/* 欠勤モーダル → イレギュラーモーダルに変更 */}
      <Modal isOpen={isAbsenceModalOpen} onClose={() => setIsAbsenceModalOpen(false)}>
        <div className="p-4">
          <h2 className="text-lg font-bold mb-4">イレギュラー登録</h2>
          
          <div className="mb-4">
            <label className="block mb-1">種別 <span className="text-red-500">*</span></label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="irregularType"
                  value="late"
                  checked={editData.irregularType === 'late'}
                  onChange={(e) => setEditData({...editData, irregularType: e.target.value})}
                  className="mr-2"
                />
                遅刻
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="irregularType"
                  value="early"
                  checked={editData.irregularType === 'early'}
                  onChange={(e) => setEditData({...editData, irregularType: e.target.value})}
                  className="mr-2"
                />
                早退
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="irregularType"
                  value="absence"
                  checked={editData.irregularType === 'absence'}
                  onChange={(e) => setEditData({...editData, irregularType: e.target.value})}
                  className="mr-2"
                />
                欠勤
              </label>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block mb-1">理由 <span className="text-red-500">*</span></label>
            <textarea
              value={editData.note}
              onChange={(e) => setEditData({...editData, note: e.target.value})}
              className="w-full border rounded px-2 py-1"
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block mb-1">報告したクライアント責任者名</label>
            <input
              type="text"
              value={editData.reportedTo}
              onChange={(e) => setEditData({...editData, reportedTo: e.target.value})}
              className="w-full border rounded px-2 py-1"
              placeholder="例: 山田様"
            />
          </div>
          
          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={() => setIsAbsenceModalOpen(false)}
              className="px-4 py-2 border rounded"
            >
              キャンセル
            </button>
            <button
              onClick={handleSaveAbsence}
              disabled={!editData.note}
              className="px-4 py-2 bg-blue-500 text-white rounded disabled:bg-gray-300"
            >
              保存
            </button>
          </div>
        </div>
      </Modal>

      {/* 社内業務モーダル */}
      {selectedRecord && (
        <InternalWorkModal
          isOpen={isInternalWorkModalOpen}
          onClose={() => setIsInternalWorkModalOpen(false)}
          date={selectedRecord.date}
          clockInTime={selectedRecord.clockInTime}
          clockOutTime={selectedRecord.clockOutTime}
          existingRecord={selectedRecord.existingRecord}
          onSubmit={handleInternalWorkSubmit}
        />
      )}

      {/* 削除確認ダイアログ */}
      <Modal isOpen={showDeleteConfirm} onClose={handleCancelDelete}>
        <div className="p-6">
          <h2 className="text-lg font-bold mb-4 text-red-600">削除確認</h2>
          {deleteTarget && (
            <div className="mb-6">
              <p className="text-gray-700 mb-2">
                以下のデータを削除しますか？
              </p>
              <div className="bg-gray-100 p-3 rounded border">
                <p><span className="font-semibold">日付:</span> {deleteTarget.dateStr}</p>
                <p><span className="font-semibold">項目:</span> {deleteTarget.fieldName}</p>
              </div>
              <p className="text-red-600 text-sm mt-2">
                ※ この操作は取り消すことができません。
              </p>
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button
              onClick={handleCancelDelete}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
            >
              キャンセル
            </button>
            <button
              onClick={handleConfirmDelete}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
            >
              削除する
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default AttendanceRecord;